import { AloftPrice, ToolkitListingID, isUndefined, syntaxFNMA } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { transactionToToolkitListingID } from '../utils/transactionToToolkitListingID.ts';
import { selectEditablePropertyDataEntities } from './editablePropertyDataSelectors.ts';

export const selectTransactionEntities = createSelector(
  [selectEditablePropertyDataEntities],
  (editablePropertyDataEntities) => {
    const entities = Object.fromEntries(
      Object.entries(editablePropertyDataEntities).map(([id, editableData]) => [
        id,
        { id, ...editableData.transaction },
      ]),
    );

    // Add a separate entry for subjectArv if it exists in editablePropertyDataEntities
    // The content should be the same as the subject transaction
    if (
      entities.subject &&
      editablePropertyDataEntities.subject &&
      'arv' in editablePropertyDataEntities.subject &&
      editablePropertyDataEntities.subject.arv
    ) {
      entities.subjectArv = { ...entities.subject };
    }

    return entities;
  },
);

export const selectTransactionById = createSelector(
  [selectTransactionEntities, (_state, id: string) => id],
  (transactionEntities, id) => transactionEntities[id],
);

export const selectAllTransactions = createSelector(
  [selectTransactionEntities],
  (transactionEntities) => Object.values(transactionEntities),
);

export const selectToolkitListingID = createSelector(
  [selectTransactionEntities, (_state, id: string) => id],
  (transactionEntities, id): ToolkitListingID | undefined =>
    transactionToToolkitListingID(transactionEntities[id]),
);

export const selectContractDate = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.contractDate,
);

export const selectSaleDate = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.saleDate,
);

export const selectSalePrice = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.price,
);

const selectSalePrices = createSelector(
  [selectTransactionEntities, selectCompSequence],
  (transactions, compSequence) =>
    Object.fromEntries(['subject', ...compSequence].map((id) => [id, transactions[id]?.price])),
);

export const selectSalePriceDisplay = createSelector([selectSalePrice], (price) => {
  if (isUndefined(price)) return 'N/A';
  return AloftPrice.schema.parse(price).toString(true);
});

export const selectSalePricesDisplay = createSelector([selectSalePrices], (salePrices) =>
  Object.fromEntries(
    Object.entries(salePrices).map(([id, price]) => [
      id,
      price ? AloftPrice.schema.parse(price).toString(true) : 'N/A',
    ]),
  ),
);

export const selectListingStatus = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.status,
);

export const selectConcessionsDisplay = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.concessions ?? 'N/A',
);

export const selectTermsOfSaleDisplay = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.termsOfSale ?? 'N/A',
);

export const selectDateOfSaleDisplay = createSelector([selectTransactionById], (transaction) => {
  // Syntax encoding fails if no status provided
  if (!transaction?.status) return '';

  return syntaxFNMA.dateOfSaleSyntax.encode({
    listingStatus: transaction?.status,
    closeMonth: transaction?.saleDate,
    contractMonth: transaction?.contractDate,
    withdrawalMonth: transaction?.withdrawalDate,
    expirationMonth: transaction?.expirationDate,
  });
});

export const selectSaleDateById = createSelector(
  [selectTransactionEntities, (_state, id: string) => id],
  (transactionEntities, id) => transactionEntities[id]?.saleDate,
);
